(function () {

    if (!window.cartThresholdSettings) return;

    const settings = window.cartThresholdSettings;

    /*
      Every cart write here goes through cart-actions.js's one queue, so a gift
      correction can't interleave with the bag sync or an upsell add and undo
      it. Looked up at call time: this file can load before cart-actions.js.
      Without it (a layout that doesn't load it), a plain fetch as before.
    */
    function cartWrite(path, payload) {
        if (window.AltenewCart && window.AltenewCart.post) {
            return window.AltenewCart.post(path, payload);
        }
        return fetch('/' + path, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });
    }

    const excludedTags = (settings.excludedTags || [])
        .map(t => (t || '').trim().toLowerCase())
        .filter(Boolean);

    /*
      Working output for the gift rules, off for everyone else.

      getEligibleCartTotal() runs on every cart read, and a single quantity
      click causes four to six of them — so the console filled with the same
      figure over and over, which is what buried everything else worth reading
      in the bag. Same switch the console noise filter uses:
      localStorage.setItem('altenew:debug', '1') and reload.
    */
    const DEBUG = (() => {
        try {
            return window.localStorage && window.localStorage.getItem('altenew:debug') === '1';
        } catch (err) {
            // Storage blocked (private mode, third-party cookie rules): stay quiet.
            return false;
        }
    })();

    function debugLog(...args) {
        if (DEBUG) console.log(...args);
    }

    // Shopify Markets conversion rate
    const currencyRate = window.Shopify?.currency?.rate || 1;

    // Convert all rewards thresholds based on currency rate
    // Thresholds come in as dollars from Liquid, need to convert to cents then apply currency rate
    const rewards = (settings.rewards || []).map(reward => ({
        ...reward,
        threshold: Number(reward.threshold), // Ensure it's a number (in dollars)
        thresholdConverted: Math.round(Number(reward.threshold) * 100 * currencyRate) // Convert to cents with rate
    })).sort((a, b) => a.thresholdConverted - b.thresholdConverted);

    /*
      "While supplies last" copy, set on the Cart Free Gift Bar section. It used
      to be shared with the Rebuy Smart Cart so the wording could not drift
      between the two arms of the cart A/B test; that cart is gone and this is
      the only reader now.

      null means the merchant has never touched the field, so the built-in
      wording stands in; "" means they cleared it on purpose, and an empty
      string is left empty. That is the whole reason Liquid does not put a
      `| default:` on these — the filter cannot tell the two apart.
    */
    const supplyNote = window.freeGiftSupplyNote || {};
    const SUPPLY_NOTE_ENABLED = supplyNote.enabled !== false;
    const SUPPLY_BAR_TEXT = supplyNote.barText ?? 'Free gifts available while supplies last.';
    const SUPPLY_ITEM_TEXT = supplyNote.itemText ?? '*while supplies last';
    const SUPPLY_COUNT_TEXT = supplyNote.countText ?? 'Only [qty] left!';
    const SUPPLY_MAX_QTY = Number(supplyNote.maxQuantity) || 0;

    /*
      The stock line for one gift, or '' when there is no honest number to show.

      Three ways to end up with nothing: the variant is not stock-tracked (Liquid
      sends null), it is sold out or oversold (<= 0 — "Only -3 left!" is worse
      than silence), or the merchant capped the note at a quantity this gift is
      still comfortably above, which is how "Only 5,000 left!" is kept out of a
      line whose whole job is urgency.
    */
    function supplyCountText(inventory) {
        if (!SUPPLY_COUNT_TEXT) return '';

        const qty = Number(inventory);
        if (!Number.isFinite(qty) || qty <= 0) return '';
        if (SUPPLY_MAX_QTY > 0 && qty > SUPPLY_MAX_QTY) return '';

        return SUPPLY_COUNT_TEXT.replace('[qty]', qty.toLocaleString());
    }

    /*
      Where each tier sits along the bar, as a percentage of its width.

      The old table put three tiers at 25 / 55 / 100 and pinned the last one to
      the right-hand end with translateX(-100%). That left a 25% run of empty
      bar before the first icon and none after the last, so the row of tiers
      read as shoved to the right rather than spread across the bar. Every tier
      is now centred on its own point, and the outer two are inset by the same
      amount, so the group is symmetrical.

      EDGE_INSET has to clear half a tier label: the box is 92px wide and the
      bar is 327px inside the drawer's padding, so 46/327 = 14.1% is the floor.
      15 leaves a few pixels of breathing room at both ends.
    */
    const EDGE_INSET = 15;

    function stepPositions(count) {
        if (count < 1) return [];
        if (count === 1) return [50];

        const span = 100 - EDGE_INSET * 2;

        return Array.from(
            { length: count },
            (_, i) => EDGE_INSET + (span * i) / (count - 1)
        );
    }

    /*
      Reads that gate a cart mutation must never be answered by a body that
      predates someone else's mutation. layout/theme.liquid dedupes concurrent
      GET /cart.js by handing every caller a clone of whatever is already in
      flight, so a read starting here could otherwise join a request issued
      before another tab's /cart/add.js landed and come back without the gift
      that is already in the bag. Its dedupe signature is the normalized URL
      with the cache-buster params (t, _, ts, timestamp, cb) stripped, so a
      param it does NOT strip gives every one of our reads a signature of its
      own and it is never merged into anybody's in-flight request.
    */
    let cartReadSeq = 0;

    function fetchCart() {
        return fetch('/cart.js?fgb=' + (++cartReadSeq), {
            credentials: 'same-origin',
            cache: 'no-store'
        }).then(r => r.json());
    }

    /*
      CROSS-TAB LOCK.

      refreshChain (below) serialises refreshes within one page. It cannot see
      a second tab: every tab runs its own copy of this file with its own
      chain, so two tabs would both read the cart, both find the gift missing
      and both POST /cart/add.js — two lines, same variant, same _free_gift
      property, different keys. Reproduced by firing a refresh in two tabs at
      the same wall-clock instant; that is the shape shoppers hit when several
      store tabs are open, or when a session is restored with several at once.

      Web Locks are same-origin and shared across every tab of the store, which
      is exactly the scope the cart lives at. Everything that reads the cart to
      decide on a mutation runs inside the lock, including the read itself — a
      lock taken after the read would protect nothing.
    */
    const CART_LOCK = 'altenew:free-gift-bar:cart';

    // Fallback for browsers with no Web Locks (pre-15.4 Safari, old Android
    // WebViews). A claim in localStorage is visible to the other tabs, and the
    // read-back confirms we actually won it rather than tying with someone.
    // The TTL means a tab that dies mid-flight frees the lock instead of
    // wedging the gift for everyone; the ceiling on attempts means a shopper
    // never loses their gift outright because a claim went stale.
    const FALLBACK_KEY = 'altenew_free_gift_lock';
    const FALLBACK_TTL_MS = 10000;
    const FALLBACK_WAIT_MS = 250;
    const FALLBACK_MAX_TRIES = 24; // ~6s, then run anyway

    function fallbackClaim(token) {
        try {
            const held = JSON.parse(localStorage.getItem(FALLBACK_KEY) || 'null');
            if (held && held.token !== token && Date.now() - held.at < FALLBACK_TTL_MS) {
                return false;
            }
            localStorage.setItem(FALLBACK_KEY, JSON.stringify({ token: token, at: Date.now() }));
            // Re-read: if another tab wrote between our read and our write, the
            // last writer wins and the loser backs off.
            const now = JSON.parse(localStorage.getItem(FALLBACK_KEY) || 'null');
            return !!now && now.token === token;
        } catch (err) {
            // Private mode / storage disabled: no lock available, so run.
            return true;
        }
    }

    function fallbackRelease(token) {
        try {
            const held = JSON.parse(localStorage.getItem(FALLBACK_KEY) || 'null');
            if (held && held.token === token) localStorage.removeItem(FALLBACK_KEY);
        } catch (err) { /* nothing to release */ }
    }

    async function withFallbackLock(fn) {
        const token = Date.now() + ':' + Math.random().toString(36).slice(2);

        for (let i = 0; i < FALLBACK_MAX_TRIES; i++) {
            if (fallbackClaim(token)) {
                try {
                    return await fn();
                } finally {
                    fallbackRelease(token);
                }
            }
            await new Promise(resolve => setTimeout(resolve, FALLBACK_WAIT_MS));
        }

        // Never starve the shopper of the gift because a claim went stale.
        return fn();
    }

    function withCartLock(fn) {
        if (navigator.locks && typeof navigator.locks.request === 'function') {
            return navigator.locks.request(CART_LOCK, fn);
        }
        return withFallbackLock(fn);
    }

    // Tags fetched straight from Shopify, keyed by product handle.
    // Avoids relying on the cart drawer's line-item DOM, which isn't
    // rebuilt until *after* a newly added item has already been evaluated.
    const productTagsCache = {};

    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('cart:build', refreshCart);
    document.addEventListener('cart:updated', refreshCart);
    document.addEventListener('ajaxProduct:added', refreshCart);

    function init() {
        renderThresholdLabels();
        updateThresholdPositions();

        // Smooth progress animation. Both copies of the bar, same as
        // updateProgressBar() — otherwise the empty-cart one jumps to its new
        // width while the other eases across.
        document.querySelectorAll('.cart-threshold-progress').forEach(bar => {
            bar.style.transition = 'width 0.35s ease';
        });

        refreshCart();
    }

    async function ensureProductTagsLoaded(cart) {
        const handles = [...new Set(cart.items.map(item => item.handle).filter(Boolean))]
            .filter(handle => !(handle in productTagsCache));

        if (!handles.length) return;

        await Promise.all(handles.map(handle =>
            fetch(`/products/${handle}.js`)
                .then(r => r.json())
                .then(product => {
                    productTagsCache[handle] = (product.tags || [])
                        .map(t => (t || '').trim().toLowerCase());
                })
                .catch(() => {
                    productTagsCache[handle] = [];
                })
        ));
    }

    /*
      Layer one of two. Every refreshCart() call is chained onto this promise
      rather than running freestanding, so the triggers that overlap within a
      single page execute one at a time. Page load alone fires two of them
      independently — this file's DOMContentLoaded -> init() -> refreshCart(),
      and layout/theme.liquid's cart-mode controller dispatching 'cart:updated'
      from applyNative() on that same load — and each cart mutation adds two
      more (the layout's fetch hook calls ShopifyCartManager.invalidate(), and
      addGift() dispatches 'cart:build'). Without the chain each of those reads
      the cart before any of the others has written to it, so every one of them
      sees a reward as "not in the bag yet" and posts /cart/add.js for it.

      Layer two is withCartLock(), because this chain is per page: a second tab
      runs its own copy of this file with its own chain and nothing here can see
      it. Both layers are needed — the chain keeps a single page cheap (no lock
      round-trip per event), the lock covers everything the page cannot see.
    */
    let refreshChain = Promise.resolve();

    function refreshCart() {
        refreshChain = refreshChain.then(runRefreshCart, runRefreshCart);
        return refreshChain;
    }

    async function runRefreshCart() {
        // This used to stand down while Rebuy's Smart Cart owned the bag. That
        // cart is gone — the native drawer is the only one now — so the bar
        // always owns the progress UI and the gift auto-add/remove.
        try {
            // Read, tidy and decide as one indivisible step, against every
            // other tab as well as this one — see withCartLock() above. The UI
            // refresh stays outside: it only reads, and holding a cross-tab
            // lock through it would stall the other tabs for no reason.
            await withCartLock(async () => {
                let cart = await fetchCart();
                await ensureProductTagsLoaded(cart);

                // Heal first, decide second. handleRewards() asks "is this gift
                // already here?", which a duplicate answers yes to — so leaving
                // duplicates in place would hide them from it forever.
                if (await reconcileGiftLines(cart)) {
                    cart = await fetchCart();
                    await ensureProductTagsLoaded(cart);
                }

                await handleRewards(cart);
            });

            const updatedCart = await fetchCart();
            await ensureProductTagsLoaded(updatedCart);
            updateUI(updatedCart);
        } catch (err) {
            // Swallowed here (not rethrown) so one failed refresh — a flaky
            // /cart.js fetch — can't permanently wedge refreshChain and block
            // every refresh after it.
            console.error('Error refreshing cart:', err);
        }
    }

    /* -----------------------------
        RENDER THRESHOLD LABELS
    ----------------------------- */

    function renderThresholdLabels() {
        const steps = document.querySelectorAll('.threshold-step');

        steps.forEach(step => {
            // Get threshold directly from DOM and convert to cents
            const thresholdValue = Number(step.dataset.threshold);
            const thresholdInCents = Math.round(thresholdValue * 100 * currencyRate);

            // Find matching reward by comparing thresholds
            const reward = rewards.find(r => {
                // Match by original threshold value (in dollars)
                return r.threshold === thresholdValue / 100;
            });

            // Update the threshold data attribute with converted value
            step.dataset.threshold = thresholdInCents;

            // Update label. "Spend" in front of the figure, because on its own
            // a number sitting above a reward name reads as the reward's price.
            const label = step.querySelector('.threshold-text');
            if (label) {
                label.textContent = `Spend ${formatMoney(thresholdInCents)}`;
            }
        });

        renderShippingLabels();
    }

    /*
      "Get $10 OFF on shipping" was a fixed string, so a shopper paying in pesos
      read a dollar figure. The merchant now writes {amount} in that text and
      sets the discount in USD beside it, and the figure lands here in whatever
      currency the market is priced in — the same treatment the thresholds get.

      Reads the template off the element rather than the text it is replacing, so
      running twice leaves the same label rather than eating the amount.
    */
    function renderShippingLabels() {
        document.querySelectorAll('.p-gift_title[data-label-template]').forEach(el => {
            const text = fillRewardAmount(el.dataset.labelTemplate, el.dataset.labelAmount);
            if (!text) return;

            el.textContent = text;
            el.setAttribute('title', text);
        });
    }

    function getItemTags(item) {
        // Primary: tags fetched directly from Shopify and cached by handle
        if (item.handle && productTagsCache[item.handle]) {
            return productTagsCache[item.handle];
        }

        // Fallback: read from the DOM element rendered by Liquid
        const el = document.querySelector(`[data-key="${item.key}"]`);
        if (el && el.dataset.productTags) {
            return el.dataset.productTags
                .split(',')
                .map(t => t.trim().toLowerCase())
                .filter(Boolean);
        }

        // Fallback: some themes pass tags via line item properties
        const tagsProp = item.properties?.__tags || item.properties?.tags || '';
        if (tagsProp) {
            return tagsProp.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        }

        return [];
    }

    function itemIsExcluded(item) {
        if (!excludedTags.length) return false;
        const itemTags = getItemTags(item);
        return excludedTags.some(tag => itemTags.includes(tag));
    }

    /*
      Whether a line carries the property addGift() stamps on it. Says who put
      the line in the bag, nothing about what it costs — reconcileGiftLines()
      uses it to know which lines are the bar's to tidy up.
    */
    function isOurGiftLine(item) {
        return !!(item.properties && item.properties._free_gift === 'true');
    }

    /*
      Whether the drawer's item list leaves this line out. templates/cart.ajax.liquid
      builds that list and hides exactly the same thing, so the "(N Items)" beside
      the title has to use this test or it describes a different cart from the one
      on screen. It used to count by reward variant id instead, and both Gift
      Variant IDs in the theme editor had a product id typed into them, so nothing
      was excluded at all: the title read "(4 Items)" over a bag showing 3.

      A gift only earns its way out of the list while it is genuinely free. Once
      Shopify charges for it, it is an ordinary line the shopper is paying for
      and has every right to see and delete — hiding it there while calling it a
      free gift below is how a $17.99 charge sat in the bag looking like a gift.
    */
    function isHiddenFromCartList(item) {
        return isOurGiftLine(item) && Number(item.final_line_price) === 0;
    }

    /*
      Whether a line is one of our gifts, for the progress maths and the YOUR
      FREE GIFTS list. Broader than the test above on purpose — a gift should
      never count toward the next threshold, however it came to be in the bag.
    */
    function isGiftLine(item, productRewardIds) {
        if (isOurGiftLine(item)) return true;
        return productRewardIds.includes(item.variant_id);
    }

    /*
      Whether some gift mechanism put this line in the bag, rather than the
      shopper choosing it. Ours stamps _free_gift; Rebuy's gift-with-purchase
      stamps its own attribution instead, which is why a Rebuy copy of a gift
      we had already added went unnoticed and got charged.

      Used only to decide what may be removed. Anything not marked here is
      treated as a deliberate purchase and is never touched, even when it is
      the same product as a gift.
    */
    function isGiftMarked(item) {
        const props = item.properties || {};
        return props._free_gift === 'true'
            || props._source === 'Rebuy'
            || /gift with purchase/i.test(props._attribution || '');
    }

    /* -----------------------------
        UI UPDATE - THRESHOLD POSITIONS
    ----------------------------- */

    function updateThresholdPositions() {
        const containers = document.querySelectorAll('.cart-threshold-steps');

        containers.forEach(container => {
            const steps = Array.from(container.querySelectorAll('.threshold-step'));
            if (!steps.length) return;

            // Sort steps by their threshold value from rewards array
            const stepsWithRewards = steps.map(step => {
                const thresholdValue = Number(step.dataset.threshold);
                const reward = rewards.find(r => r.thresholdConverted === thresholdValue);
                return { step, threshold: thresholdValue, reward };
            }).sort((a, b) => a.threshold - b.threshold);

            // Reorder in DOM
            stepsWithRewards.forEach(({ step }) => container.appendChild(step));

            // Distribute positions evenly
            const positions = stepPositions(steps.length);

            stepsWithRewards.forEach((item, index) => {
                item.step.style.left = `${positions[index]}%`;
            });

            reserveStepSpace(container);
            watchStepHeights(container);
        });
    }

    /*
      The tiers on the "has items" bar are positioned absolutely, so they take
      up no height of their own and the stylesheet holds the room open with a
      fixed padding underneath. That was a magic number sized to a label of
      three lines, which is why the names used to be cut short. Measure the
      tallest tier instead and hold open exactly that much, so a name is free
      to wrap onto as many lines as it needs.
    */
    function reserveStepSpace(container) {
        const wrapper = container.closest('.cart-threshold-wrapper');
        if (!wrapper) return;

        const steps = Array.from(container.querySelectorAll('.threshold-step'));
        if (!steps.length) return;

        // The empty-bag copy lays its tiers out in flow, so it already grows to
        // fit them and must keep the padding the stylesheet gives it.
        if (getComputedStyle(steps[0]).position !== 'absolute') return;

        const tallest = Math.max(...steps.map(step => step.getBoundingClientRect().height));

        // A closed drawer measures zero. Leave the stylesheet's own reserve
        // alone until there is something real to measure.
        if (tallest > 0) {
            wrapper.style.paddingBottom = `${Math.ceil(tallest) + 8}px`;
        }
    }

    /*
      The drawer is display:none until it is opened, so the first measurement
      above usually comes back as zero. Watching the tiers means the reserve is
      set the moment the drawer is opened for the first time, and again if a
      label rewraps — on a narrower phone, or when the reward names change.
    */
    function watchStepHeights(container) {
        if (typeof ResizeObserver === 'undefined') return;
        if (container.dataset.heightWatched) return;
        container.dataset.heightWatched = 'true';

        // Changing the wrapper's padding does not resize a tier, so this
        // cannot feed back into itself.
        const observer = new ResizeObserver(() => reserveStepSpace(container));
        container.querySelectorAll('.threshold-step').forEach(step => observer.observe(step));
    }

    function updateUI(cart) {
        // Hide the whole rewards bar whenever an excluded-tag item is in the cart
        const wrappers = document.querySelectorAll('.cart-threshold-wrapper');
        const blocked = hasExcludedItem(cart);

        wrappers.forEach(wrapper => {
            wrapper.style.display = blocked ? 'none' : '';
        });

        // Get all product rewards variant IDs
        const productRewardIds = rewards
            .filter(r => r.type === 'product')
            .map(r => Number(r.variantId));

        // Update the "(N Items)" counter in the drawer header. Counts exactly
        // what the list below it shows — see isHiddenFromCartList().
        const visibleItemCount = cart.items.reduce((count, item) => {
            if (isHiddenFromCartList(item)) {
                return count;
            }
            return count + item.quantity;
        }, 0);

        const countLabel = `(${visibleItemCount} Item${visibleItemCount !== 1 ? 's' : ''})`;

        const countEl = document.querySelector('[data-count]');
        if (countEl) {
            countEl.textContent = countLabel;
        }

        // The subtotal carries the same count. It gets its own attribute because
        // [data-count] is also on .cart__items and on unrelated sliders, so a
        // querySelectorAll over it would overwrite the cart item list.
        const subtotalCountEl = document.querySelector('[data-subtotal-count]');
        if (subtotalCountEl) {
            subtotalCountEl.textContent = countLabel;
        }

        // Calculate cart total excluding gift items
        const total = getEligibleCartTotal(cart);

        // Update progress bar
        updateProgressBar(total);

        // Update header text and remaining amount
        updateHeader(total);

        // "Only 237 left! | Free gifts available while supplies last."
        updateSupplyNote(total);

        // Update active states
        updateActiveStates(total);

        // Render free gifts
        renderFreeGifts(cart, productRewardIds);

        // Re-measure the room the tiers need. The observer in
        // watchStepHeights() covers a label rewrapping, but its callbacks are
        // part of the rendering loop, which a browser skips entirely while the
        // page is in a background tab. Every cart event runs through here, so
        // this is the path that does not depend on the page being on screen.
        document.querySelectorAll('.cart-threshold-steps').forEach(reserveStepSpace);

        // Toggle "Away From" text on every header copy
        document.querySelectorAll('.cart-threshold-header').forEach(header => {
            const toReceiveEl = header.querySelector('.to-receive');
            const titleEl = header.querySelector('.threshold-title');

            if (toReceiveEl && titleEl) {
                toReceiveEl.style.display =
                    titleEl.textContent === 'All rewards unlocked 🎉'
                        ? 'none'
                        : 'inline-block';
            }
        });
    }

    function updateProgressBar(total) {
        if (!rewards.length) return;

        // Same duplicate-markup situation as updateThresholdPositions(): both
        // the "has items" and "empty cart" copies need their fill updated.
        const bars = document.querySelectorAll('.cart-threshold-progress');
        if (!bars.length) return;

        const sorted = [...rewards].sort((a, b) => a.thresholdConverted - b.thresholdConverted);

        // Same table the icons are placed with, so the fill always stops under
        // the tier it has actually reached.
        const positions = stepPositions(sorted.length);

        let progress = 0;

        if (total < sorted[0].thresholdConverted) {
            // Before first threshold
            progress = (total / sorted[0].thresholdConverted) * positions[0];
        } else if (total >= sorted[sorted.length - 1].thresholdConverted) {
            // Past all thresholds. The last tier sits at EDGE_INSET in from the
            // right, so run the fill on to the end of the track rather than
            // stopping under it — a bar with a gap left in it reads as "still
            // something to unlock", which is the opposite of what has happened.
            progress = 100;
        } else {
            // Between thresholds
            for (let i = 0; i < sorted.length - 1; i++) {
                const current = sorted[i];
                const next = sorted[i + 1];

                if (total >= current.thresholdConverted && total < next.thresholdConverted) {
                    const ratio = (total - current.thresholdConverted) /
                        (next.thresholdConverted - current.thresholdConverted);

                    progress = positions[i] + ratio * (positions[i + 1] - positions[i]);
                    break;
                }
            }
        }

        bars.forEach(bar => {
            bar.style.width = Math.min(progress, 100) + '%';
        });
    }

    function updateHeader(total) {
        const headers = document.querySelectorAll('.cart-threshold-header');
        if (!headers.length) return;

        const sorted = [...rewards].sort((a, b) => a.thresholdConverted - b.thresholdConverted);

        // Find next unmet threshold
        const nextReward = sorted.find(r => total < r.thresholdConverted);

        headers.forEach(header => {
            const titleEl = header.querySelector('.threshold-title');
            const subtitleEl = header.querySelector('.threshold-subtitle');
            const worthEl = header.querySelector('.threshold-worth');

            if (!titleEl || !subtitleEl) return;

            // The tiers below are labelled by reward type now, so this line is the
            // only place the next reward is named. Cleared first so a gift's value
            // never lingers over a shipping tier.
            if (worthEl) worthEl.textContent = '';

            if (!nextReward) {
                // All rewards unlocked
                titleEl.textContent = 'All rewards unlocked 🎉';
                subtitleEl.textContent = '';
                return;
            }

            // Update title based on reward type
            switch (nextReward.type) {
                case 'product':
                    titleEl.textContent = nextReward.title || 'Free Gift';
                    if (worthEl && nextReward.worth) {
                        worthEl.textContent = `Worth ${nextReward.worth}`;
                    }
                    break;
                case 'shipping':
                    titleEl.textContent = settings.isInternational
                        ? fillRewardAmount(
                              nextReward.internationalText,
                              nextReward.internationalAmount
                          ) || 'Discounted Shipping'
                        : 'FREE SHIPPING';
                    break;
                case 'discount':
                    titleEl.textContent = nextReward.label || 'Get Discount';
                    break;
                default:
                    titleEl.textContent = 'Unlock Reward';
            }

            // Update remaining amount
            const remaining = nextReward.thresholdConverted - total;
            subtitleEl.innerHTML = `<b>${formatMoney(remaining)}</b>`;
        });
    }

    /*
      The note under the progress bar. It describes a gift, so it is only shown
      while there is a gift still to unlock — past the last product tier the bar
      is talking about shipping or a discount code, and "while supplies last"
      would be answering a question nobody asked.

      The count is narrower still: it only appears when the very next reward is
      that gift, because it is the header line directly above that says which
      gift is meant. Under "$75.00 Away From FREE SHIPPING" a bare "Only 74
      left!" is a number about nothing — so a cart short of the shipping tier
      gets the promise without the figure, and picks the figure up as soon as
      the gift becomes the thing being worked toward.

      Emptied rather than hidden with a class so the aria-live region has
      nothing left to announce once the reason for it is gone.
    */
    function updateSupplyNote(total) {
        const notes = document.querySelectorAll('.cart-threshold-supply-note');
        if (!notes.length) return;

        let countText = '';
        let barText = '';

        if (SUPPLY_NOTE_ENABLED) {
            const sorted = [...rewards].sort((a, b) => a.thresholdConverted - b.thresholdConverted);
            const nextReward = sorted.find(r => total < r.thresholdConverted);
            const giftStillLocked = sorted.some(r => r.type === 'product' && total < r.thresholdConverted);

            if (giftStillLocked) {
                barText = SUPPLY_BAR_TEXT;

                if (nextReward && nextReward.type === 'product') {
                    countText = supplyCountText(nextReward.inventory);
                }
            }
        }

        notes.forEach(note => {
            note.textContent = '';

            if (countText) {
                const countEl = document.createElement('span');
                countEl.className = 'supply-note-count';
                countEl.textContent = countText;
                note.appendChild(countEl);
            }

            if (countText && barText) {
                // Decorative: a screen reader reading "Only 237 left! pipe Free
                // gifts available..." is worse than a beat of silence.
                const sepEl = document.createElement('span');
                sepEl.className = 'supply-note-separator';
                sepEl.setAttribute('aria-hidden', 'true');
                sepEl.textContent = '|';
                note.appendChild(sepEl);
            }

            if (barText) {
                const textEl = document.createElement('span');
                textEl.className = 'supply-note-text';
                textEl.textContent = barText;
                note.appendChild(textEl);
            }
        });
    }

    function updateActiveStates(total) {
        document.querySelectorAll('.threshold-step').forEach(step => {
            const threshold = Number(step.dataset.threshold || 0);
            step.classList.toggle('active', total >= threshold);
        });
    }

    /* -----------------------------
        FREE GIFTS RENDER
    ----------------------------- */

    /*
      The stock line for one gift already in the bag. Shorter wording than the
      progress bar's: the shopper has the gift, so all that is left to say is
      that keeping it depends on stock. Returns '' when there is nothing to say,
      which keeps the row exactly as it was before this existed.
    */
    function giftSupplyMarkup(inventory) {
        if (!SUPPLY_NOTE_ENABLED) return '';

        const countText = supplyCountText(inventory);
        if (!countText && !SUPPLY_ITEM_TEXT) return '';

        const parts = [];
        if (countText) parts.push(`<span class="supply-note-count">${escapeHtml(countText)}</span>`);
        if (countText && SUPPLY_ITEM_TEXT) {
            parts.push('<span class="supply-note-separator" aria-hidden="true">|</span>');
        }
        if (SUPPLY_ITEM_TEXT) {
            parts.push(`<span class="supply-note-text">${escapeHtml(SUPPLY_ITEM_TEXT)}</span>`);
        }

        return `<div class="free-gift-supply">${parts.join('')}</div>`;
    }

    function escapeHtml(value) {
        return String(value).replace(/[&<>"']/g, ch => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
        }[ch]));
    }

    /*
      THEME EDITOR ONLY — is the gift actually free?

      The rows below print every gift at formatMoney(0). That is the promise the
      tier makes, not a fact read back from the cart, so a gift Shopify is still
      charging for looks identical to one it is giving away. Between 31 Aug and
      3 Sep 2026 that gap put 130 orders through at full price for a "free" gift
      and nobody could see it from the drawer.

      The gift lives in two places that nothing keeps in step: the tier above
      names the product, and an automatic discount in Shopify is what actually
      zeroes the line. Swap one without the other and shoppers pay.

      So check the cart instead of trusting the tier. final_line_price is what
      Shopify will charge after its own discount allocations, so anything above
      zero on a gift line means the discount did not cover it — whatever the
      reason. That catches all of them: a discount still naming last month's
      gift, a product renamed underneath it, or "FREE GIFT 2" refusing to fire
      because it gives two items free and only one is in the bag.

      Shopify.designMode is true only inside the theme editor, so this reaches
      whoever just changed the tier and never a shopper.
    */
    function giftChargeWarning(gifts) {
        if (!window.Shopify || !window.Shopify.designMode) return '';

        const charged = gifts.filter(item => Number(item.final_line_price) > 0);
        if (!charged.length) return '';

        const many = charged.length > 1;
        const names = charged
            .map(item => `${escapeHtml(item.product_title)} (${formatMoney(item.final_line_price)})`)
            .join(', ');

        return `
            <div class="free-gift-warning" role="alert">
                <p class="free-gift-warning__title">
                    ${many ? 'These gifts are' : 'This gift is'} being charged, not given away
                </p>
                <p>
                    ${names} ${many ? 'are in the bag as free gifts' : 'is in the bag as a free gift'}, but Shopify is
                    still charging for ${many ? 'them' : 'it'} — so a shopper who earns
                    ${many ? 'them' : 'it'} pays that at checkout. A charged gift the bag
                    already lists shows there at its real price; one the bag hides keeps a
                    red row below.
                </p>
                <p>
                    The automatic discount has to name the same product as the tier.
                    <a href="https://admin.shopify.com/store/altenew/discounts"
                       target="_blank" rel="noopener">Open discounts in Shopify</a>
                </p>
                <p class="free-gift-warning__scope">
                    Only you can see this. It shows in the theme editor, never on the live site.
                </p>
            </div>
        `;
    }

    function renderFreeGifts(cart, productRewardIds) {
        /*
          Every gifts box on the page, not just the drawer's.

          The bag page is a second view of the same cart, and cart.ajax.liquid
          hides a free gift from the item list there exactly as it does in the
          drawer, because the list is shared markup. The drawer made up for that
          with this box. The bag page had no box, so a gift that was in the cart
          and already free never appeared on /cart at all — it was being written
          into the drawer sitting hidden on that same page. The gift reached
          checkout free the whole time; there was simply nowhere on the page a
          shopper could see it, which reads as the gift not having worked.

          Keyed off the attribute rather than the id so both boxes can sit on
          one page without two elements answering to #free-gifts.
        */
        const containers = document.querySelectorAll('[data-free-gifts]');
        if (!containers.length) return;

        // What each gift is worth, so the row can show the value struck out
        // beside the free price. This used to be read back off a
        // data-compare-price attribute on the tier's product photo; the photo
        // is an icon now, so it comes straight from the reward instead.
        const comparePrices = {};
        // Stock per gift, for the "Only 10 left! | *while supplies last" line
        // under each unlocked gift's name.
        const inventories = {};
        rewards
            .filter(r => r.type === 'product')
            .forEach(reward => {
                comparePrices[reward.variantId] = reward.worth || '';
                inventories[reward.variantId] = reward.inventory;
            });

        // Every line the bar treats as a gift. The theme-editor warning reads
        // this one, so it still names a charged gift after the list below has
        // dropped it.
        const giftLines = cart.items.filter(item => isGiftLine(item, productRewardIds));

        /*
          What actually belongs under YOUR FREE GIFTS: the lines that are free.

          Shopify runs one "Buy X get Y" discount per order. A second gift in
          the same bag — Rebuy's gift-with-purchase, or a duplicate line — is
          charged at full price, so it is not a free gift, and listing it here
          told the shopper it was one.

          Nothing goes missing by dropping it. isHiddenFromCartList() only hides
          a gift while it is free, so the moment Shopify charges for one it
          reappears in the bag list above as an ordinary line, at its real price,
          with a delete control.
        */
        const gifts = giftLines.filter(item => Number(item.final_line_price) === 0);

        const warning = giftChargeWarning(giftLines);

        if (!gifts.length) {
            containers.forEach(el => { el.innerHTML = warning; });
            return;
        }

        const html = `
            ${warning}
            <div class="free-gifts-wrapper">
                <h3 class="free-gifts-title">YOUR FREE GIFTS:</h3>
                ${gifts.map(item => {
                    const comparePrice = comparePrices[item.variant_id] || '';
                    const supplyMarkup = giftSupplyMarkup(inventories[item.variant_id]);

                    /*
                      What this row costs, read back from the cart rather than
                      assumed. It used to print formatMoney(0) flat — the tier's
                      promise, not the cart's answer — so a gift Shopify was
                      still charging for looked exactly like one it was giving
                      away. That is how 130 orders went out between 31 Aug and
                      3 Sep 2026 charged for a "free" gift with the bag showing
                      $0.00 on every one of them.

                      final_line_price is the price after Shopify's own discount
                      allocations, so it is 0 when the discount really did fire
                      and the full price when it did not. The struck-out worth
                      only goes alongside it while the gift is genuinely free —
                      "$21.00 $21.00" reads as a bug, and striking out a price
                      the shopper is about to pay is worse than saying nothing.
                    */
                    const linePrice = Number(item.final_line_price) || 0;
                    const isFree = linePrice === 0;

                    return `
                        <div class="free-gift-item">
                            <img
                                src="${item.image}"
                                width="40"
                                height="40"
                                alt="${item.product_title}"
                            />
                            <div class="free-gift-info">
                                <div class="free-gift-name">${item.product_title}</div>
                            </div>
                            <div class="free-gift-price${isFree ? '' : ' is-charged'}">
                                ${comparePrice && isFree ? `
                                    <span style="text-decoration: line-through; color: #999; margin-right: 8px;">
                                        ${comparePrice}
                                    </span>
                                ` : ''}
                                <span>${formatMoney(linePrice)}</span>
                            </div>
                            ${supplyMarkup}
                        </div>
                    `;
                }).join('')}
            </div>
        `;

        containers.forEach(el => { el.innerHTML = html; });
    }

    /* -----------------------------
        REWARD LOGIC
    ----------------------------- */

    function hasExcludedItem(cart) {
        return cart.items.some(item => itemIsExcluded(item));
    }

    /*
      SELF-HEALING PASS.

      The lock stops two tabs racing from here on, but it cannot fix a bag that
      is already wrong — a shopper who hit the race yesterday still has two
      gift lines today, and their cart survives until they check out. It also
      catches what no lock can: a tab killed between its /cart/add.js and the
      response, a gift line whose quantity a shopper (or an app) nudged above
      one, and a _free_gift line left behind after a tier was repointed at a
      different product in the theme editor.

      Everything goes in a single /cart/update.js keyed by line item key.
      Sequential /cart/change.js calls would be a fresh race of their own —
      each one renumbers the lines behind it — and would leave the bag in a
      half-fixed state if one failed.
    */
    async function reconcileGiftLines(cart) {
        const rewardVariantIds = rewards
            .filter(r => r.type === 'product' && r.variantId)
            .map(r => Number(r.variantId));

        const updates = {};

        // A tier that no longer points at this product: the line is a gift
        // nobody is offering any more, so it must not stay in the bag at zero
        // — or, worse, get charged for.
        cart.items.forEach(item => {
            if (!isOurGiftLine(item)) return; // not one of ours
            if (!rewardVariantIds.includes(Number(item.variant_id))) {
                updates[item.key] = 0;
            }
        });

        /*
          One line per gift.

          This bar is not the only thing putting gifts in the bag. Rebuy's
          gift-with-purchase adds the same products from its own rules, and it
          stamps its own properties rather than ours — so the old pass, which
          only ever looked at _free_gift lines, could not see the second copy.

          Shopify runs one "Buy X get Y" discount per order. The moment a gift
          has two lines, only one of them is zeroed and the shopper is charged
          full price for the other. That is what put a Floral Cameo in the bag
          twice, once at $0.00 and once at $17.99.

          So keep the line Shopify actually discounted and drop the rest. That
          choice also settles the bag: whoever added the surviving line sees it
          still there and does not add another, whereas dropping the free one
          would start a delete-and-re-add loop on every page load.

          Only gift-marked lines are ever dropped. A shopper who deliberately
          bought the gift product keeps their line, whatever it costs.
        */
        rewardVariantIds.forEach(variantId => {
            const lines = cart.items.filter(item => (
                Number(item.variant_id) === variantId && updates[item.key] === undefined
            ));
            if (!lines.length) return;

            const keep = lines.find(item => Number(item.final_line_price) === 0) || lines[0];

            lines.forEach(item => {
                if (item.key === keep.key) return;
                if (!isGiftMarked(item)) return; // theirs to keep
                updates[item.key] = 0;
            });

            // A gift line a shopper (or an app) nudged above one.
            if (isGiftMarked(keep) && keep.quantity !== 1) updates[keep.key] = 1;
        });

        if (!Object.keys(updates).length) return false;

        console.warn('[free-gift-bar] correcting gift lines:', updates);

        await cartWrite('cart/update.js', { updates: updates }).then(r => r.json());

        return true;
    }

    /*
      GIFT CHARGED BUT NOT FREE.

      handleRewards() decides on the bag's own total, and that total can clear a
      threshold while Shopify still refuses to make the gift free: the gift only
      counts items no other promo has touched, so a bag full of promo items
      qualifies on paper and is charged in practice. Neither branch below fired
      in that case — the bag qualified and the gift was present — so the line
      just sat there with a price on it. That is the shape that put 111 charged
      gifts through in three days.

      Taking the line back out is the easy half. The hard half is not putting it
      straight back: the next refresh would see a qualifying bag with no gift and
      add one, which is charged again, removed again, forever. So the shape of
      the bag at the moment of the refusal is remembered, and the gift is not
      offered again until the shopper actually changes something.

      Held in localStorage against the cart token. sessionStorage was scoped to
      one tab while the cart it guards is shared by every tab in the browsing
      session, so opening the bag in a second tab lost the note and put the
      charged gift straight back — the shape reported from incognito testing,
      where working across a couple of tabs is normal. Stamping the token keeps
      the old guarantee: the note cannot outlive the cart it was written for, so
      it can never wedge a shopper's gift.
    */
    const SUPPRESS_KEY = 'altenew_gift_suppressed';

    function cartShapeSignature(cart, eligibleTotal) {
        /*
          Composition of everything except gift lines. Adding or taking back a
          gift must not change the signature, or the removal itself would read as
          a new bag and the gift would go straight back in — the loop this whole
          mechanism exists to stop.

          Every gift-marked line, not just ours: a gift Rebuy added is one this
          bar can now take back, so leaving it in here would move the signature
          the moment the removal landed and defeat the note it just wrote.
        */
        const parts = cart.items
            .filter(item => !isGiftMarked(item))
            .map(item => item.variant_id + 'x' + item.quantity)
            .sort();
        return eligibleTotal + '|' + parts.join(',');
    }

    function readSuppressed(cart) {
        try {
            const held = JSON.parse(localStorage.getItem(SUPPRESS_KEY) || 'null');
            // A different cart is a different shopper's bag as far as this is
            // concerned. Drop the note rather than apply it to the new one.
            if (!held || !held.token || held.token !== cart.token) return {};
            return held.gifts || {};
        } catch (err) {
            return {};
        }
    }

    function isGiftSuppressed(variantId, signature, cart) {
        return readSuppressed(cart)[String(variantId)] === signature;
    }

    function suppressGift(variantId, signature, cart) {
        try {
            const gifts = readSuppressed(cart);
            gifts[String(variantId)] = signature;
            localStorage.setItem(SUPPRESS_KEY, JSON.stringify({
                token: cart.token,
                gifts: gifts
            }));
        } catch (err) {
            // Private mode and a full quota both land here. Losing the note only
            // costs one retry on the next refresh, so there is nothing to do.
        }
    }

    async function handleRewards(cart) {
        /*
          No isProcessing flag any more. It only ever guarded the inside of this
          function, while every caller reaches it through refreshChain (one at a
          time in this page) and withCartLock (one at a time across every tab),
          so it could not fire — and if it ever did, it silently skipped the
          shopper's gift rather than deferring it.
        */
        try {
            const blocked = hasExcludedItem(cart);
            const total = getEligibleCartTotal(cart);
            const signature = cartShapeSignature(cart, total);

            // Handle product rewards
            const productRewards = rewards.filter(r => r.type === 'product');

            for (const reward of productRewards) {
                // A tier with no product picked resolves to no variant at all.
                // Adding one anyway posts a junk id to /cart/add.js, which is
                // how a tier with an unresolvable variant ended up putting the
                // gift in the bag at full price.
                if (!reward.variantId) continue;

                /*
                  "Is the gift already in the bag?" means one some gift
                  mechanism put there, not any line that happens to be the same
                  product. A shopper who buys the gift product outright must not
                  read as having been given it — that is what made the reward
                  look present so it could be taken away again.

                  It used to count only this bar's own lines, so a gift Rebuy had
                  already added read as absent and the bar added a second copy on
                  top. Shopify discounts one of the two and charges for the other,
                  which is the duplicate-line shape reported from the bag.
                  reconcileGiftLines() cleans that up on the next pass, but the
                  bag should not need cleaning up in the first place.
                */
                const hasReward = cart.items.some(i => (
                    i.variant_id == reward.variantId && isGiftMarked(i)
                ));
                const qualifies = !blocked && total >= reward.thresholdConverted;

                /*
                  Our own line, with a price on it. Whatever the cause — a
                  competing promo eating the qualifying items, a tier pointing at
                  the wrong product, a discount that ended mid-session — the
                  shopper is being charged for something they never chose. Take
                  it back out, and remember this bag so it is not re-added.
                */
                const chargedGift = cart.items.some(i => (
                    i.variant_id == reward.variantId
                    && isGiftMarked(i)
                    && Number(i.final_line_price) > 0
                ));

                if (chargedGift) {
                    suppressGift(reward.variantId, signature, cart);
                    await removeGift(reward.variantId, cart);
                    return;
                }

                if (qualifies && !hasReward) {
                    // Refused for this exact bag already. Leave it alone until
                    // the shopper changes something, then try again.
                    if (isGiftSuppressed(reward.variantId, signature, cart)) continue;
                    await addGift(reward.variantId);
                    return;
                }

                if (!qualifies && hasReward) {
                    await removeGift(reward.variantId, cart);
                    return;
                }
            }

        } catch (err) {
            console.error('Error handling rewards:', err);
        }
    }

    /* -----------------------------
        ADD GIFT
    ----------------------------- */

    function addGift(variantId) {
        return cartWrite('cart/add.js', {
            id: Number(variantId),
            quantity: 1,
            properties: {
                "_free_gift": "true"
            }
        })
            .then(r => r.json())
            .then(() => {
                document.dispatchEvent(new CustomEvent('cart:build'));
            })
            .catch(err => {
                console.error('Error adding gift:', err);
            });
    }

    /* -----------------------------
        REMOVE GIFT
    ----------------------------- */

    function removeGift(variantId, cart) {
        // Only ever take back a line some gift mechanism put in the bag — ours
        // or Rebuy's. Matching on the variant alone made this delete a shopper's
        // own purchase of the gift product, which is why the test is on the
        // line's own attribution rather than what it costs or what it is.
        const item = cart.items.find(i => i.variant_id == variantId && isGiftMarked(i));

        if (!item) {
            return Promise.resolve();
        }

        return cartWrite('cart/change.js', {
            id: item.key,
            quantity: 0
        })
            .then(r => r.json())
            .then(() => {
                document.dispatchEvent(new CustomEvent('cart:build'));
            })
            .catch(err => {
                console.error('Error removing gift:', err);
            });
    }

    /* -----------------------------
        FORMAT MONEY
    ----------------------------- */

    function formatCurrencySymbol() {
        return (settings.currencySymbol || '').replace(/^[A-Z]+/, '');
    }

    /*
      Puts a reward's amount into its label. The amount is set in USD next to the
      text, the same unit the thresholds use, so it goes through the same
      multiply before it is formatted.

      An amount of 0 means the merchant left the field empty, and the token comes
      out along with the space that was holding it open rather than leaving
      "Get  OFF on shipping" behind.
    */
    function fillRewardAmount(template, usdAmount) {
        const text = (template || '').trim();
        if (!text.includes('{amount}')) return text;

        const amount = Number(usdAmount);
        if (!amount) return text.replace('{amount}', '').replace(/\s{2,}/g, ' ').trim();

        return text.replace('{amount}', formatMoney(Math.round(amount * 100 * currencyRate)));
    }

    function formatMoney(cents) {
        const locale = document.documentElement.lang || 'en';

        const formatted = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: settings.activeCurrency,
            currencyDisplay: 'code'
        }).format(cents / 100);

        let result = formatted.replace(settings.activeCurrency, formatCurrencySymbol()).trim();

        if (settings.activeCurrency === 'CAD') {
            result += ' CAD';
        }

        return result;
    }

    function getEligibleCartTotal(cart) {
        // Get all product reward variant IDs
        const productRewardIds = rewards
            .filter(r => r.type === 'product')
            .map(r => Number(r.variantId));

        /*
          Every line some gift mechanism put in the bag comes out of the total,
          plus anything carrying an excluded tag.

          It used to drop only this bar's own lines, which let a gift Rebuy had
          added — and that Shopify was charging for — count toward the next
          threshold. A shopper cannot earn a gift by having been given one, and
          the removal of such a line has to leave the total where it was or the
          suppression note below is keyed to a bag that stops existing the
          instant the line goes.

          It used to drop every line whose variant matched a reward, which meant
          a shopper who simply bought the gift product had it excluded from
          their own total. With an otherwise empty bag that read as $0, the
          reward looked present but unearned, and handleRewards() deleted the
          item they had just chosen — the gift products could not be bought at
          all unless the bag was already over the threshold without them.
        */
        const eligibleTotal = cart.items.reduce((sum, item) => {
            if (isGiftMarked(item) || itemIsExcluded(item)) {
                return sum;
            }
            return sum + item.final_line_price;
        }, 0);

        debugLog('Cart total excluding gifts and excluded tags:', eligibleTotal / 100);

        return eligibleTotal;
    }

})();